import React, { useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import LayoutAction from "../actions/LayoutAction";

interface HoverManagerProps {
  itemId: string;
  itemType: string;
  label: string;
  children: React.ReactNode;
  delay?: number;
  closeDelay?: number;
  projectId?: string;
}

const HoverManager: React.FC<HoverManagerProps> = ({
  itemId,
  itemType,
  label,
  children,
  delay = 300,
  closeDelay = 300,
  projectId,
}) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHoverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      clearHoverTimeout();

      timeoutRef.current = setTimeout(() => {
        const payload = {
          value: itemId,
          label,
          type: itemType,
          projectId,
          isOpen: true,
        };
        dispatch(LayoutAction.hoverDataItem(payload));
      }, delay);
    },
    [dispatch, itemId, label, itemType, projectId, delay, clearHoverTimeout]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearHoverTimeout();
      const relatedTarget = e.relatedTarget;

      let isMovingToTooltip = false;

      if (
        relatedTarget &&
        relatedTarget instanceof Element &&
        typeof relatedTarget.closest === "function"
      ) {
        isMovingToTooltip = !!(
          relatedTarget.closest('[data-tooltip-id="information-tooltip"]') ||
          relatedTarget.closest(".crm-tooltip") ||
          relatedTarget.closest(".react-tooltip") ||
          relatedTarget.closest('[role="tooltip"]') ||
          relatedTarget.closest('[id="information-tooltip"]') ||
          relatedTarget.closest('div[style*="position: fixed"]') ||
          relatedTarget.closest('div[style*="z-index: 10000"]')
        );
      }

      if (!isMovingToTooltip) {
        timeoutRef.current = setTimeout(() => {
          dispatch(
            LayoutAction.hoverDataItem({
              isOpen: false,
              value: "",
              onlyIfCurrentItem: itemId,
            })
          );
        }, closeDelay);
      }
    },
    [dispatch, closeDelay, clearHoverTimeout, itemId]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearHoverTimeout();
      dispatch(
        LayoutAction.hoverDataItem({
          isOpen: false,
          value: "",
          onlyIfCurrentItem: itemId,
        })
      );
    },
    [dispatch, clearHoverTimeout, itemId]
  );

  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, [clearHoverTimeout]);

  return (
    <div
      data-tooltip-id="information-tooltip"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={{ position: "relative" }}
    >
      {children}
    </div>
  );
};

export default HoverManager;
